import { LocaleCode } from '@tink/config/src/markets';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { FormattedMessage } from 'react-intl';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { PageView } from '../../../models/analytics';
import { LegalTextType } from '../../../core/components/ClientLegalDisclaimer/ClientLegalDisclaimer';
import { DocumentOverlayLink } from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { messages } from '../../messages';

type LocaleDocumentRef = {
  locale: LocaleCode;
  termsUrl: string;
  privacyUrl: string;
};

type ClientLegalText = {
  type: LegalTextType;
  defaultLocale: LocaleDocumentRef;
  supportedLocales: LocaleDocumentRef[];
};

type LegalMapping = Record<string, ClientLegalText>;

export const clientMapping: LegalMapping = {
  '38478face84645e39774fd824920d8f7': {
    type: LegalTextType.INTERNAL,
    defaultLocale: {
      locale: LocaleCode.EN,
      termsUrl:
        'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/en_US/terms-and-conditions-23-05-23-v2.html',
      privacyUrl:
        'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/en_US/privacy-policy.html-23-05-23',
    },
    supportedLocales: [
      {
        locale: LocaleCode.DE,
        termsUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/de_DE/terms-and-conditions-23-05-23-v2.html',
        privacyUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/de_DE/privacy-policy-23-05-23.html',
      },
      {
        locale: LocaleCode.EN,
        termsUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/en_US/terms-and-conditions-23-05-23-v2.html',
        privacyUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/en_US/privacy-policy-23-05-23.html',
      },
      {
        locale: LocaleCode.ES,
        termsUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/es_ES/terms-and-conditions-23-05-23-v2.html',
        privacyUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/es_ES/privacy-policy-23-05-23.html',
      },
      {
        locale: LocaleCode.IT,
        termsUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/it_IT/terms-and-conditions-23-05-23-v2.html',
        privacyUrl:
          'https://cdn.tink.se/link/configuration/oxford-production/38478face84645e39774fd824920d8f7/legal/it_IT/privacy-policy-23-05-23.html',
      },
    ],
  },
};

export type GetClientLegalMappingsArgs = {
  clientId: string;
  locale: LocaleCode;
  clientMappings?: LegalMapping;
};

export const getClientLegalMappings = ({
  clientId,
  locale,
  clientMappings = clientMapping,
}: GetClientLegalMappingsArgs) => {
  if (!clientMapping[clientId]) return undefined;

  const preferredLocale = clientMappings[clientId].supportedLocales.find(
    (entry) => entry.locale === locale
  );

  if (!preferredLocale) return clientMappings[clientId].defaultLocale;

  return preferredLocale;
};

export const ERROR_CLIENT_LEGAL_DOCS_NOT_CONFIGURED = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.CLIENT_LEGAL_DOCS_NOT_CONFIGURED,
};

export type MerchantConsentClientLegalProps = {
  clientName: string;
  clientId: string;
  locale: LocaleCode;
};

export const MerchantConsentClientLegal = ({
  clientName,
  clientId,
  locale,
}: MerchantConsentClientLegalProps) => {
  const urlMap = getClientLegalMappings({ clientId, locale });

  if (!urlMap) throw new ApplicationError(ERROR_CLIENT_LEGAL_DOCS_NOT_CONFIGURED);

  return (
    <FormattedMessage
      {...messages.merchantConsentTermsText}
      values={{
        clientName,
        termsAndConditions: (
          <DocumentOverlayLink
            documentUrl={urlMap.termsUrl}
            pageView={PageView.TERMS_AND_CONDITION_SCREEN}
          >
            <FormattedMessage {...messages.merchantConsentTermsLabel} />
          </DocumentOverlayLink>
        ),
        privacyPolicy: (
          <DocumentOverlayLink
            documentUrl={urlMap.privacyUrl}
            pageView={PageView.PRIVACY_POLICY_SCREEN}
          >
            <FormattedMessage {...messages.merchantConsentPolicyLabel} />
          </DocumentOverlayLink>
        ),
      }}
    />
  );
};
